import { IonCheckbox, IonPage, IonText, useIonRouter } from "@ionic/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import { usePostHog } from "posthog-js/react";
import { z } from "zod";
import { DynamicAvatar } from "@/components/CoreValue/DynamicAvatar";
import { Button } from "@/components/Global/Button";
import { Content } from "@/components/Global/Content";
import { FullscreenLoadingSpinner } from "@/components/Loading/FullscreenLoadingSpinner";
import { LoadingText } from "@/components/Loading/LoadingText";
import { backendService } from "@/lib/backend";
import { queryOptions_coreValues } from "@/lib/query/functions/core-values/all";
import { queryOptions_coreValuesSelected } from "@/lib/query/functions/core-values/selected";
import { useQueryStream } from "@/lib/query/hooks/useQueryStream";
import { Routes } from "@/lib/router";
import { useSelectorModalStore } from "@/lib/store/modal";
import { cx } from "@/lib/style/cva.config";
import { jsonParse } from "@/utils/json/parse";

export function FriendCheckGenerate() {
	const { t } = useTranslation();
	const router = useIonRouter();
	const [selectedValues, setSelectedValues] = useState<string[]>([]);
	const posthog = usePostHog();
	const present = useSelectorModalStore.use.present();

	const [streamedCoreValues, queryCoreValuesStream] = useQueryStream({
		queryKey: ["core-values", "friend-check", "analysis-stream"],
		queryFn: (params) =>
			backendService.corevalues.coreValuesControllerGenerateCoreValueAnalysisStream(
				{ type: "friendcheck" },
				params,
			),
		enabled: true,
	});

	const [streamedSummary, querySummaryStream] = useQueryStream({
		queryKey: ["core-values", "friend-check", "summary-stream"],
		queryFn: (params) =>
			backendService.corevalues.coreValuesControllerGenerateCoreValueSummaryStream(
				{ type: "friendcheck" },
				params,
			),
		enabled: queryCoreValuesStream.isSuccess,
	});

	const parsedSummary = jsonParse({
		input: streamedSummary || "",
		schema: z.object({ summary: z.string().nullish() }),
	});

	const parsedCoreValues = jsonParse({
		input: streamedCoreValues || "[]",
		schema: z.array(
			z
				.object({
					name: z.string().nullish(),
					score: z.coerce.number().nullish(),
					reason: z.string().nullish(),
				})
				.passthrough(),
		),
	});

	const { data: allCoreValues } = useQuery({
		...queryOptions_coreValues(),
	});
	const { data: selectedCoreValues } = useQuery({
		...queryOptions_coreValuesSelected(),
	});

	const onConfirmedAddCoreValues = async () => {
		posthog.capture("FriendCheck Analysis Add Core Values", {
			coreValues: selectedValues,
		});
		await backendService.corevalues.selectedCoreValuesControllerCreate(
			selectedValues.map((selectedCoreValue) => {
				const coreValue = parsedCoreValues?.find(
					(coreValue) => coreValue.name === selectedCoreValue,
				);
				return {
					name: selectedCoreValue,
					matchScore: coreValue?.score ?? 0,
					reason: coreValue?.reason ?? "",
				};
			}),
		);
		router.push(Routes.Dashboard());
	};

	const isLoading = !parsedSummary?.summary && parsedCoreValues?.length === 0;
	const isDone = querySummaryStream.isSuccess && queryCoreValuesStream.isSuccess;

	return (
		<IonPage>
			<Content>
				{isLoading ? (
					<FullscreenLoadingSpinner text={t("friend-check.analysis.generating")} />
				) : (
					<>
						<div className="flex flex-col gap-2 py-4">
							<DynamicAvatar accuracyType="none" className="pt-6 pb-10" />
							<IonText className="text-brown-600 text-xl font-bold">
								{t("friend-check.analysis.what-friends-say")}
							</IonText>
							<div className="bg-brown-100 rounded-xl p-4">
								<IonText data-mask-text className="text-brown-700 text-lg font-semibold">
									{parsedSummary?.summary ? parsedSummary?.summary : <LoadingText />}
								</IonText>
							</div>
						</div>
						<AnimatePresence>
							{!!parsedCoreValues && (
								<motion.div
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									transition={{ duration: 0.2 }}
									className="flex flex-col gap-3"
								>
									<div className="flex flex-col">
										<IonText className="text-brown-600 text-xl font-bold">
											{t("friend-check.analysis.core-values")}
										</IonText>
										<IonText className="text-brown-600">
											{t("friend-check.analysis.core-values-description")}
										</IonText>
									</div>
									<div className="flex flex-col gap-2">
										{parsedCoreValues?.map((parsedCoreValue, index) => {
											const coreValue = allCoreValues?.find(
												(coreValue) => coreValue.name === parsedCoreValue.name,
											);
											const alreadyInList = selectedCoreValues?.some(
												(selectedCoreValue) => selectedCoreValue.coreValue.id === coreValue?.id,
											);
											const checked =
												selectedValues.includes(coreValue?.name || "") || alreadyInList;

											return (
												<motion.div
													key={index}
													className="flex flex-1"
													initial={{
														opacity: 0,
														scaleX: 0.75,
														y: "1rem",
														display: "none",
													}}
													animate={{
														opacity: 1,
														scaleX: 1,
														y: 0,
														display: "flex",
													}}
													transition={{ duration: 0.25, delay: 2 + index * 0.25 }}
												>
													<IonCheckbox
														data-attr="modal.friend-check.analysis.generate.core-value"
														className={cx(
															"bg-brown-100 part-[label]:flex part-[label]:flex-1 part-[label]:items-center part-[label]:justify-between part-[label]:gap-2 flex-1 rounded-xl p-3",
															alreadyInList && "part-container:opacity-50 !opacity-100",
														)}
														checked={checked}
														disabled={alreadyInList}
														value={coreValue?.name}
														onIonChange={(event) => {
															setSelectedValues((values) => {
																const value = String(event.detail.value);
																if (values.includes(value)) {
																	return values.filter((v) => v !== value);
																} else {
																	return [...values, value];
																}
															});
														}}
													>
														{/* <div className="flex h-full w-full flex-1 items-center justify-between"> */}
														<span className="text-brown-700 flex flex-row items-center gap-2 font-semibold">
															<IonText className="block min-w-5 text-center">
																{coreValue?.emoji || "❓"}
															</IonText>
															<IonText>{coreValue?.name || coreValue?.id || ""}</IonText>
														</span>

														{alreadyInList && (
															<IonText className="text-brown-400 text-xs font-normal">
																{t(
																	"modal.friend-check.analysis.generate.core-values.already-in-list",
																)}
															</IonText>
														)}
														{/* </div> */}
													</IonCheckbox>
												</motion.div>
											);
										})}
									</div>
								</motion.div>
							)}
							{isDone && (
								<motion.div
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									transition={{ duration: 0.2 }}
								>
									<Button
										className="w-full"
										disabled={selectedValues.length === 0}
										onClick={() => {
											present("friendCheckAnalysisConfirm", {
												newCoreValues: selectedValues.map((coreValue) => ({
													name: coreValue,
													emoji:
														allCoreValues?.find((_coreValue) => coreValue === _coreValue.name)
															?.emoji ?? "❓",
												})),
												onConfirmed: onConfirmedAddCoreValues,
											});
										}}
									>
										{t("friend-check.analysis.add", { count: selectedValues.length })}
									</Button>
								</motion.div>
							)}
						</AnimatePresence>
					</>
				)}
			</Content>
		</IonPage>
	);
}
